import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Typography from "@material-ui/core/Typography";

import { DataStructure } from "@/components/StaticTable";
import { Filter } from "@/types/ApiResponse";
import { Operator } from "../Filters/operators";
import ChampsSelect from "@/components/StaticTable/FiltersPopin/champSelect";
import { OperateurSelect } from "@/components/StaticTable/FiltersPopin/operateurSelect";
import { ValeurSelect } from "@/components/StaticTable/FiltersPopin/valeurSelect";

interface FiltersProps<T> {
  dataStructure: DataStructure<T>[];
  selectedcolumn: DataStructure<T>;
  onAddNewFiltre: (newFilter: Filter) => void;
  onEditFiltre: (oldFilter: Filter, newFilter: Filter) => void;
  defaultKey?: string;
  defaultOperateur?: Operator;
  defaultValeur?: string | number | Date;
  defaultValeurMin?: string | number | Date;
  defaultValeurMax?: string | number | Date;
}

export function FiltersPopin<T>({
  selectedcolumn,
  dataStructure,
  onAddNewFiltre,
  onEditFiltre,
  defaultKey,
  defaultOperateur,
  defaultValeur,
  defaultValeurMax,
  defaultValeurMin,
}: FiltersProps<T>) {
  const { t } = useTranslation();

  const [selectedFiltreKey, setSelectedFiltreKey] = useState<string | null>(
    defaultKey || ""
  );
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(
    defaultOperateur || null
  );
  const [filterValue, setFilterValue] = useState<number | string | Date>(
    defaultValeur || ""
  );
  const [filterValueMin, setFilterValueMin] = useState<number | string | Date>(
    defaultValeurMin || ""
  );
  const [filterValueMax, setFilterValueMax] = useState<number | string | Date>(
    defaultValeurMax || ""
  );
  const [erroneMinMax, setErroneMinMax] = useState(false);

  const isModificationFiltre = !!defaultKey && !!defaultOperateur;

  const isBetweenOperatorSelected = useCallback(
    () => selectedOperator?.value === "BETWEEN" || false,
    [selectedOperator]
  );

  const isNullOperatorSelected = () =>
    selectedOperator?.value === "IS_NULL" || false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRowType = () => {
    for (const ds of dataStructure) {
      if (!ds.filter) continue;

      const filters = Array.isArray(ds.filter) ? ds.filter : [ds.filter];

      // @ts-ignore
      const matchingFilter = filters.find((f) => f.key === selectedFiltreKey);
      if (matchingFilter) return matchingFilter.rowType;
    }
    return undefined;
  };

  const hasValue = isNullOperatorSelected()
    ? true
    : isBetweenOperatorSelected()
    ? !!(filterValueMin && filterValueMax)
    : !!filterValue;

  const isValidationBtnDisabled =
    erroneMinMax || !selectedcolumn || !selectedOperator || !hasValue;

  const handleFilterValidation = useCallback(() => {
    if (!!selectedFiltreKey && !!selectedOperator && !isValidationBtnDisabled) {
      const filterValueParsed =
        getRowType() === "number"
          ? parseFloat(filterValue as string)
          : getRowType() === "date"
          ? new Date(filterValue).toJSON()
          : filterValue;

      const newFilter = {
        key: selectedFiltreKey,
        operateur: selectedOperator.value,
        ...(!isBetweenOperatorSelected() && {
          value: filterValueParsed,
        }),
        ...(getRowType() === "number" &&
          isBetweenOperatorSelected() && {
            valueMin: filterValueMin,
            valueMax: filterValueMax,
          }),
        ...(getRowType() === "date" &&
          isBetweenOperatorSelected() && {
            valueMin: new Date(filterValueMin),
            valueMax: new Date(filterValueMax),
          }),
      };

      if (isModificationFiltre) {
        const oldFiltre = {
          key: defaultKey,
          operateur: defaultOperateur?.value,
          value: defaultValeur,
          valueMin: defaultValeurMin,
          valueMax: defaultValeurMax,
        };
        onEditFiltre(oldFiltre as Filter, newFilter as Filter);
      } else {
        onAddNewFiltre(newFilter as Filter);
      }
    }
  }, [
    selectedFiltreKey,
    selectedOperator,
    isValidationBtnDisabled,
    getRowType,
    filterValue,
    filterValueMin,
    filterValueMax,
    isBetweenOperatorSelected,
    isModificationFiltre,
    defaultKey,
    defaultOperateur?.value,
    defaultValeur,
    defaultValeurMin,
    defaultValeurMax,
    onEditFiltre,
    onAddNewFiltre,
  ]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleFilterValidation();
      }
    };

    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
  }, [handleFilterValidation]);

  /*const textPlaceHolder =
  selectedOperator?.value === "IN"
    ? t("common.dmri.filter.placeHolderValueIn")
    : t("common.dmri.filter.placeHolderValue");*/

  return (
    <div style={{ margin: 10 }}>
      <div
        style={{
          display: "flex",
          marginBottom: 10,
          gap: 10,
        }}
      >
        <ChampsSelect
          dataStructure={dataStructure}
          selectedcolumn={selectedcolumn as DataStructure<T>}
          setSelectedFiltreKey={setSelectedFiltreKey}
          selectedFiltreKey={selectedFiltreKey}
        />
        <OperateurSelect
          selectedcolumn={selectedcolumn as DataStructure<T>}
          selectedFiltreKey={selectedFiltreKey}
          selectedOperator={selectedOperator}
          setSelectedOperator={setSelectedOperator}
        />
        <ValeurSelect
          dataStructure={dataStructure}
          selectedFiltreKey={selectedFiltreKey}
          isBetweenOperatorSelected={isBetweenOperatorSelected}
          isNullOperatorSelected={isNullOperatorSelected}
          filterValue={filterValue}
          filterValueMin={filterValueMin}
          filterValueMax={filterValueMax}
          setFilterValue={setFilterValue}
          setFilterValueMin={setFilterValueMin}
          setFilterValueMax={setFilterValueMax}
          setErroneMinMax={setErroneMinMax}
        />
      </div>
      {erroneMinMax && (
        <Typography style={{ color: "red", marginBottom: 5 }}>
          {t("common.dmri.filter.erreurMinMax")}
        </Typography>
      )}
      <div style={{ display: "flex" }}>
        <Button
          onClick={handleFilterValidation}
          color="primary"
          variant="contained"
          disabled={isValidationBtnDisabled}
          style={{ margin: "auto" }}
        >
          {t("common.dmri.filter.btnApply")}
        </Button>
      </div>
    </div>
  );
}
